import { useState, useEffect } from 'react';
import { computeDateRange } from '../common/SectionDateFilter';
import { useSeoReportStore } from '../../store/seoReportStore';
import { useSearchParams } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Tooltip as InfoTooltip } from '../common/Tooltip';
import Card from '../common/Card';
import Button from '../common/Button';
import Spinner from '../common/Spinner';
import { Sk } from '../common/Skeleton';
import Badge from '../common/Badge';
import { getGoogleAuthUrl } from '../../api/searchConsole.api';
import {
  useGoogleStatus,
  useGoogleDisconnect,
  useGscStatus,
  useGscProperties,
  useGscLink,
  useGscUnlink,
  useGscPerformance,
  useGscInsights,
} from '../../hooks/useSearchConsole';
import { themeColor } from './colorThemes';
import { useIsViewer, useIsAdmin } from '../../hooks/useRole';
import TopQueriesTable from './TopQueriesTable';
import TopPagesTable from './TopPagesTable';
import DeviceBreakdown from './DeviceBreakdown';
import CountryBreakdown from './CountryBreakdown';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

function formatNumber(n) {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function KpiCard({ label, value, color, tooltip }) {
  const labelEl = (
    <span className="text-xs font-medium font-label text-brand-on-surface-variant dark:text-brand-outline uppercase tracking-wider">
      {label}
    </span>
  );
  return (
    <div className="rounded-xl border border-brand-outline-variant dark:border-brand-outline p-4 flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
        {tooltip ? (
          <InfoTooltip content={tooltip} placement="top">
            <span className="cursor-help underline decoration-dotted underline-offset-2 decoration-brand-outline/60">{labelEl}</span>
          </InfoTooltip>
        ) : labelEl}
      </div>
      <span className="text-2xl font-bold font-label text-brand-on-surface dark:text-white tabular-nums">{value}</span>
    </div>
  );
}

// ========== Not connected state ==========
function ConnectState({ siteId }) {
  const [loading, setLoading] = useState(false);

  const handleConnect = async () => {
    setLoading(true);
    try {
      const { authUrl } = await getGoogleAuthUrl();
      window.location.href = authUrl;
    } catch {
      setLoading(false);
    }
  };

  return (
    <Card>
      <div className="text-center py-8">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
          <svg className="w-7 h-7 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
        </div>
        <h3 className="text-base font-semibold text-brand-on-surface dark:text-brand-outline-variant mb-1">
          Connect Google Search Console
        </h3>
        <p className="text-sm text-brand-on-surface-variant dark:text-brand-outline mb-5 max-w-sm mx-auto">
          Sign in with your Google account to view clicks, impressions, CTR, and average position for your site.
        </p>
        <Button onClick={handleConnect} isLoading={loading} disabled={loading}>
          {loading ? 'Redirecting to Google...' : 'Connect Google Account'}
        </Button>
      </div>
    </Card>
  );
}

// ========== Property selector state ==========
function PropertySelector({ siteId }) {
  const { properties, isLoading } = useGscProperties(siteId, true);
  const linkMutation = useGscLink(siteId);
  const disconnectMutation = useGoogleDisconnect();
  const [selected, setSelected] = useState('');

  useEffect(() => {
    if (properties.length > 0 && !selected) {
      setSelected(properties[0].siteUrl);
    }
  }, [properties, selected]);

  return (
    <Card>
      <div className="py-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-brand-on-surface dark:text-brand-outline-variant">
            Select Search Console Property
          </h3>
          <button
            onClick={() => disconnectMutation.mutate()}
            disabled={disconnectMutation.isPending}
            className="text-xs text-red-500 hover:text-red-600 hover:underline font-label"
          >
            Disconnect Google
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-4">
            <Spinner size="sm" />
          </div>
        ) : properties.length === 0 ? (
          <p className="text-sm text-brand-outline dark:text-brand-on-surface-variant text-center py-4">
            No properties found in your Google Search Console. Make sure your site is added to Search Console.
          </p>
        ) : (
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs text-brand-on-surface-variant dark:text-brand-outline mb-1 font-label">Property</label>
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-brand-outline-variant dark:border-brand-outline bg-brand-surface-container-lowest dark:bg-brand-on-surface text-brand-on-surface dark:text-brand-outline-variant focus:ring-2 focus:ring-brand-primary-container focus:border-brand-500"
              >
                {properties.map((p) => (
                  <option key={p.siteUrl} value={p.siteUrl}>
                    {p.siteUrl} ({p.permissionLevel})
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => linkMutation.mutate(selected)}
              isLoading={linkMutation.isPending}
              disabled={!selected || linkMutation.isPending}
            >
              Link Property
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

// ========== Performance dashboard ==========
function PerformanceDashboard({ siteId, themeKey, viewMode }) {
  const period = useSeoReportStore((s) => s.period);
  const customFrom = useSeoReportStore((s) => s.customFrom);
  const customTo = useSeoReportStore((s) => s.customTo);
  const dateRange = computeDateRange(period, customFrom, customTo);
  const { performance, isLoading, error } = useGscPerformance(siteId, period, dateRange);
  const { insights, isLoading: insightsLoading } = useGscInsights(siteId, period, dateRange);
  const unlinkMutation = useGscUnlink(siteId);
  const disconnectMutation = useGoogleDisconnect();
  const isViewer = useIsViewer();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Card>
          <Sk className="h-4 w-28 mb-5 rounded-full" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border border-brand-outline-variant dark:border-brand-outline p-4 flex flex-col gap-3">
                <Sk className="h-2.5 w-14 rounded-full" />
                <Sk className="h-6 w-20" />
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <Sk className="h-4 w-24 mb-4 rounded-full" />
          <Sk className="h-64 w-full rounded-xl" />
        </Card>
      </div>
    );
  }

  if (error) {
    const errMsg = error.response?.data?.error?.message || 'Failed to load Search Console data';
    const busy = unlinkMutation.isPending || disconnectMutation.isPending;
    return (
      <Card>
        <div className="text-center py-8">
          <p className="text-sm text-red-500 dark:text-red-400 mb-4">{errMsg}</p>
          {!isViewer && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="secondary"
                onClick={() => unlinkMutation.mutate()}
                isLoading={unlinkMutation.isPending}
                disabled={busy}
              >
                Change Property
              </Button>
              <Button
                onClick={() => disconnectMutation.mutate()}
                isLoading={disconnectMutation.isPending}
                disabled={busy}
              >
                Reconnect Google
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  }

  const totals = performance?.totals || { clicks: 0, impressions: 0, ctr: 0, position: 0 };
  const daily = performance?.daily || [];

  return (
    <div className="space-y-4">
      <Card>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium font-label uppercase">
              Search Console
            </span>
            {performance?.fetchedAt && (
              <span className="text-[11px] text-brand-outline dark:text-brand-on-surface-variant font-label">
                Last update: {timeAgo(performance.fetchedAt)}
              </span>
            )}
          </div>
          {!isViewer && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => unlinkMutation.mutate()}
                disabled={unlinkMutation.isPending}
                className="text-xs text-brand-outline hover:text-red-500 transition-colors font-label"
                title="Unlink property"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            label="Total Clicks"
            value={formatNumber(totals.clicks)}
            color={themeColor(themeKey, 0)}
            tooltip="Times someone clicked through to your site from a Google search result."
          />
          <KpiCard
            label="Total Impressions"
            value={formatNumber(totals.impressions)}
            color={themeColor(themeKey, 1)}
            tooltip="Times any of your pages appeared in Google search results."
          />
          <KpiCard
            label="Average CTR"
            value={`${(totals.ctr * 100).toFixed(2)}%`}
            color={themeColor(themeKey, 2)}
            tooltip="Click-through rate: clicks ÷ impressions. Higher = your snippets are more compelling."
          />
          <KpiCard
            label="Average Position"
            value={totals.position ? totals.position.toFixed(1) : '—'}
            color={themeColor(themeKey, 3)}
            tooltip="Average rank of your pages in search results. Lower = better."
          />
        </div>
      </Card>

      {/* Daily trend chart */}
      {viewMode === 'charts' && daily.length > 1 && (
        <Card>
          <h4 className="text-sm font-semibold text-brand-on-surface dark:text-brand-outline-variant mb-4">
            Daily Trends
          </h4>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={daily} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
                <defs>
                  <linearGradient id="gscClicksGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={themeColor(themeKey, 0)} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={themeColor(themeKey, 0)} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gscImpressionsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={themeColor(themeKey, 1)} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={themeColor(themeKey, 1)} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  tickFormatter={(d) => {
                    const date = new Date(d);
                    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  }}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                  }}
                  formatter={(value, name) => {
                    if (name === 'clicks') return [formatNumber(value), 'Clicks'];
                    if (name === 'impressions') return [formatNumber(value), 'Impressions'];
                    return [value, name];
                  }}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="clicks"
                  stroke={themeColor(themeKey, 0)}
                  strokeWidth={2}
                  fill="url(#gscClicksGrad)"
                  name="Clicks"
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="impressions"
                  stroke={themeColor(themeKey, 1)}
                  strokeWidth={2}
                  fill="url(#gscImpressionsGrad)"
                  name="Impressions"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* SEO Insights: Queries, Pages, Devices, Countries */}
      {insightsLoading ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card><Sk className="h-48 w-full rounded-xl" /></Card>
          <Card><Sk className="h-48 w-full rounded-xl" /></Card>
        </div>
      ) : insights ? (
        <>
          {viewMode === 'details' && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <Card>
                <TopQueriesTable queries={insights.queries} themeKey={themeKey} />
              </Card>
              <Card>
                <TopPagesTable pages={insights.pages} themeKey={themeKey} />
              </Card>
            </div>
          )}

          {viewMode === 'charts' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <DeviceBreakdown devices={insights.devices} themeKey={themeKey} />
              </Card>
              <Card>
                <CountryBreakdown countries={insights.countries} themeKey={themeKey} />
              </Card>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

// ========== Viewer-only read-only notice ==========
function ViewerNotConfigured({ message }) {
  return (
    <Card>
      <div className="text-center py-8">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-brand-surface-container-high dark:bg-brand-on-surface flex items-center justify-center">
          <svg className="w-6 h-6 text-brand-outline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-sm font-semibold text-brand-on-surface dark:text-brand-outline mb-1">Search Console Not Configured</h3>
        <p className="text-xs text-brand-on-surface-variant dark:text-brand-outline max-w-sm mx-auto font-label">{message}</p>
      </div>
    </Card>
  );
}

// ========== Main export ==========
export default function SearchConsoleSection({ siteId, themeKey, viewMode }) {
  const { googleStatus, isLoading: googleLoading } = useGoogleStatus();
  const { gscStatus, isLoading: gscLoading } = useGscStatus(siteId);
  const isViewer = useIsViewer();
  const isAdmin = useIsAdmin();

  // Handle Google OAuth callback redirect
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const authResult = searchParams.get('google_auth');
    if (authResult) {
      // Clean up the URL param
      searchParams.delete('google_auth');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (googleLoading || gscLoading) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <Spinner size="sm" />
        </div>
      </Card>
    );
  }

  // State 1: Google not connected
  if (!googleStatus?.connected) {
    if (!isAdmin) {
      return <ViewerNotConfigured message="Google account not connected. Contact a workspace admin to connect Google Search Console." />;
    }
    return <ConnectState siteId={siteId} />;
  }

  // State 2: Connected but no property linked
  if (!gscStatus?.linked) {
    if (isViewer) {
      return <ViewerNotConfigured message="No Search Console property linked to this site. Contact the site owner to link a property." />;
    }
    return <PropertySelector siteId={siteId} />;
  }

  // State 3: Fully connected — show dashboard
  return <PerformanceDashboard siteId={siteId} themeKey={themeKey} viewMode={viewMode} />;
}
